import { createSelector } from 'reselect'
import config from 'constants/config'
import { setCookie } from 'lib/web/cookieUtils'
import type { ExperimentId } from 'constants/experiments'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import { DurationSeconds } from 'lib/datetime/durationInSeconds'

export function setTriggeredCookie(flagKey: ExperimentId, provider: App.ExperimentProvider) {
  const providerPrefix = provider === 'cherto' ? 'cherto_' : ''

  setCookie(`${providerPrefix}${flagKey}_triggered`, 't', DurationSeconds.SIXTY_DAYS, '/', 'Lax')
}

const allFeatureFlags = new Set(Object.values(OptimizelyFeatureFlags))
export function isFeatureFlagExperiment(experimentId: ExperimentId | OptimizelyFeatureFlags): experimentId is OptimizelyFeatureFlags {
  return typeof experimentId === 'string' && allFeatureFlags.has(experimentId as OptimizelyFeatureFlags)
}

type OptimizelyExperimentDecisionVariation = string | boolean | undefined

export const getOptimizelyExperimentDecisionVariation = createSelector(
  (_: App.State, experimentId: ExperimentId) => experimentId,
  (state: App.State, experimentId: ExperimentId) => state.optimizely.optimizelyExperiments[experimentId],
  (experimentId, experiment): OptimizelyExperimentDecisionVariation => {
    // For devs to set a specific variation locally. Set REACT_APP_OPTIMIZELY_[experimentId]=true for variant or false for control in .env,
    if (config[`OPTIMIZELY_${experimentId}`] !== undefined) { return config[`OPTIMIZELY_${experimentId}`] }

    return experiment?.variationKey || undefined
  },
)

export const getOptimizelyExperimentVariation = createSelector(
  (_: App.State, experimentId: ExperimentId) => experimentId,
  (state: App.State, experimentId: ExperimentId) => state.optimizely.optimizelyExperiments[experimentId],
  (experimentId, experiment): string | undefined => {
    // For devs to set a specific variation locally. Set REACT_APP_OPTIMIZELY_[experimentId]=true for variant or false for control in .env,
    if (config[`OPTIMIZELY_${experimentId}`] !== undefined) { return config[`OPTIMIZELY_${experimentId}`] }
    if (!experiment?.enabled) {
      return
    }

    if (experiment.enabled && Object.keys(experiment.variables).length) {
      return experiment.variables[experiment.variationKey]
    }

    return experiment.variationKey
  },
)

export function getOptimizelyFeatureFlagVariation(state: App.State, experimentId: OptimizelyFeatureFlags) {
  // For devs to force a flag locally. Set REACT_APP_OPTIMIZELY_[flagKey]=true to turn it on or
  // false to turn it off in .env, the same override the experiment selectors above accept.
  // Only those two values speak for a flag; a variation name left over from experiment use
  // (=on, =variant_1) says nothing about on or off, so the real decision still stands.
  const localOverride = config[`OPTIMIZELY_${experimentId}`]
  if (typeof localOverride === 'boolean') return localOverride

  return !!state.optimizely.optimizelyFeatureFlags[experimentId]
}

export function getExperimentEnabledDecision(experimentVariationKey: string | null | undefined): boolean {
  const normalizedVariationKey = experimentVariationKey ? String(experimentVariationKey).toLowerCase() : undefined
  return !!(normalizedVariationKey && (normalizedVariationKey !== 'off' && normalizedVariationKey !== 'control'))
}

// exclude all gha redirections from the experiment
export function isGHARedirection(utmContent?: string): boolean {
  return utmContent === 'gha-lpp' || utmContent === 'gha-lpc' || utmContent === 'gha-vr'
}

export function isAllowedToTriggerExperiment(canTrigger: boolean, utmContent?: string, experimentId?: string): boolean {
  return canTrigger && (!isGHARedirection(utmContent) || !!config.ALLOWED_METASEARCH_EXPERIMENTS?.includes(experimentId || ''))
}
